import { Grid } from "@material-ui/core";
import { Formik } from "formik";
import { connect } from "react-redux";

import FormTextField from "../../shared/form-text-field";
import InputSubtitle from "../../shared/input-subtitle";
import { SearchTicketsFormSchema } from "../schema/search-tickets-form-schema";
import { SearchTicketsFormProps } from "../../../interface/forms/SearchTicketsFormProps";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { getContractorCompanies } from "../../../store/contractorStore";
import { getTransportCompanies } from "../../../store/transportStore";
import Button from "../../shared/Button";
import useFocusSearch from "../../../hooks/useFocusSearch";
import FormErrorMessage from "../../shared/form-error-message";
import FormSelectSearchField from "../../shared/form-select-search-field";

const SearchTicketsForm = ({
  searchTicketsFormValues,
  onSubmit,
  onSearchTicketNumberChange,
  onClear,
  searchFormRef,
}: SearchTicketsFormProps) => {
  const contractorCompanies = useAppSelector(getContractorCompanies);
  const transportCompanies = useAppSelector(getTransportCompanies);
  const { focusOnSearchInput } = useFocusSearch("ticketNumber");

  return (
    <Formik
      innerRef={searchFormRef}
      initialValues={searchTicketsFormValues}
      validationSchema={SearchTicketsFormSchema}
      onSubmit={(values, { setSubmitting }) => onSubmit(values, setSubmitting)}
      enableReinitialize={true}
    >
      {({ values, handleSubmit, isSubmitting, setFieldValue, resetForm }) => (
        <>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name={"ticketNumber"}
                  label={"Ticket Number"}
                  placeholder="Search Ticket Number"
                  onChange={(e: { target: { value: string } }) =>
                    onSearchTicketNumberChange(
                      values,
                      e.target.value,
                      setFieldValue
                    )
                  }
                  value={values.ticketNumber ?? ""}
                />
                <FormErrorMessage name="ticketNumber" />
                <InputSubtitle text="*Auto Search Enabled" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormSelectSearchField
                  name="contractorCompanyId"
                  placeholder="Select Contractor"
                  value={
                    contractorCompanies
                      .map((c) => ({ label: c.name, value: c.id }))
                      .find(
                        (opt) => opt.value === values.contractorCompanyId
                      ) || 0
                  }
                  options={contractorCompanies.map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                  onChange={(selectedOption: any) => {
                    setFieldValue(
                      "contractorCompanyId",
                      selectedOption ? selectedOption.value : 0
                    );
                  }}
                  isClearable
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormSelectSearchField
                  name="transportCompanyId"
                  placeholder="Select Haulage Co."
                  value={
                    transportCompanies
                      .map((c) => ({ label: c.name, value: c.id }))
                      .find((opt) => opt.value === values.transportCompanyId) ||
                    0
                  }
                  options={transportCompanies.map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                  onChange={(selectedOption: any) => {
                    setFieldValue(
                      "transportCompanyId",
                      selectedOption ? selectedOption.value : 0
                    );
                  }}
                  isClearable
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name={"jobSite"}
                  label={"Job Site"}
                  value={values.jobSite ?? ""}
                  placeholder="Search Job Site"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name={"truckNumber"}
                  label={"Truck Number"}
                  value={values.truckNumber ?? ""}
                  placeholder="Search Truck Number"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name={"truckLicensePlate"}
                  label={"Truck License Plate"}
                  value={values.truckLicensePlate ?? ""}
                  placeholder="Search License Plate"
                />
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Button
                  label={isSubmitting ? "Searching..." : "Search"}
                  type="submit"
                  disabled={isSubmitting}
                  variant="contained"
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  label={isSubmitting ? "Clearing..." : "Clear"}
                  type="button"
                  disabled={isSubmitting}
                  variant="contained"
                  onClick={() => {
                    focusOnSearchInput();
                    resetForm();
                    onClear();
                  }}
                  color={"default"}
                />
              </Grid>
            </Grid>
          </form>
        </>
      )}
    </Formik>
  );
};

export default connect()(SearchTicketsForm);
